import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../Components/layouts";
import { checkValidEmail, notify } from '../../utils';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { Paths } from '../../Routes/routePaths'
import { UserServices } from '../../Services/UserServices';
import CircularProgress from '@material-ui/core/CircularProgress';
import UserBulkUpload from './components/userBulkUpload';
import { ProjectServices } from '../../Services/ProjectServices';
import { useAuthDispatch, useAuthState } from '../../Context';
import { USER_ROLES, REQUIRED_ERROR } from '../../utils/constants';
import {
    applyProfileToPayload,
    emptyProfileFields,
    formatApiErrorMessage,
    validateProfileFields,
} from '../../utils/userProfileFields';
import { associatedProjectId, loggedInRole, readStoredUserAuth } from '../../utils/associatedProjectId';
import CreateUserForm from './components/CreateUserForm';
import { useHistory } from 'react-router';
import commonStyles, { pageTitleStyles } from 'theme/commonStyles';
import { useImpersonation } from '../../hooks/useImpersonation';
import { IMPERSONATION_BLOCKED_MESSAGE } from '../../Services/impersonationSession';
import {
    clearInactiveProgrammeFields,
    normalizeProgrammeOptions,
    profileFieldsForProgramme,
} from '../../utils/programmeOptions';

const userServices = new UserServices();
const projectService = new ProjectServices();

export const useStyles = makeStyles((theme) => ({
    inputStyle: {
        borderColor: '#838383',
        borderRadius: '25px',
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        margin: '0 0 24px'
    },
    heading: {
        ...pageTitleStyles
    },
    btn: {
        padding: '8px 16px',
        minHeight: 40,
        height: 40,
        fontSize: '13px',
        color: 'white',
        borderRadius: '5px',
        marginLeft: '15px',
    },
    cancelBtn: {
        padding: '8px 16px',
        minHeight: 40,
        height: 40,
        background: '#B7B7B7',
        fontSize: '13px',
        color: 'white',
        borderRadius: '5px',
        marginLeft: '15px',
        "&:hover": {
            background: "#454343"
        }
    },
    placeBtn: {
        display: 'flex',
        justifyContent: 'end',
        padding: '20px 0',
        marginTop: 20
    }
}));

const initFormState = {
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    location: '',
    role: '',
    sub_role: '',
    project: null,
    ...emptyProfileFields(),
};

const AddUsers = () => {

    const classes = useStyles();
    const commonClasses = commonStyles();

    const dispatch = useAuthDispatch();
    const { userDetails } = useAuthState(dispatch);
    const history = useHistory();
    const impersonation = useImpersonation();

    const [loading, setLoading] = React.useState(false);
    const [openBulkUploadDialog, setOpenBulkUploadDialog] = React.useState(false);
    const [companyList, setCompanyList] = React.useState([]);
    const [customers, setCustomers] = React.useState([]);
    const [formState, setFormState] = React.useState({ ...initFormState });
    const [roleList, setRoleList] = React.useState([]);
    const [projectList, setProjectList] = React.useState([]);
    const [programmeOptions, setProgrammeOptions] = React.useState(null);

    React.useEffect(() => {
        getCustomerList();
        projectService.getProgrammeOptions()
            .then(result => {
                if (result?.status) setProgrammeOptions(normalizeProgrammeOptions(result));
            })
            .catch(error => notify('error', error?.response?.data?.message || 'Could not load programme options.'));
    }, []);

    const getCustomerList = async () => {
        const cusResult = await projectService.getCustomerList();
        if (cusResult && cusResult.data) {
            setCompanyList(cusResult.data);
        }
    }

    const getProjectList = async (csId) => {
        const projectRes = await projectService.allProjectsByCustomer(csId);
        if (projectRes?.status) {
            const allProject = projectRes?.results?.map(item => {
                return {
                    name: item.project_name,
                    id: item.project_uuid,
                    programme_type: item.programme_type || '',
                    programme_type_label: item.programme_type_label || '',
                    programme_subtype: item.programme_subtype || '',
                }
            }) || [];
            setProjectList(allProject);
        }
    };

    React.useEffect(() => {
        const { role } = userDetails;
        let roles = [];
        if (role === USER_ROLES.SUPER_ADMIN) {
            roles = [{
                id: 'customeradmin',
                name: 'Customer Admin'
            },
            {
                id: 'csmadmin',
                name: 'CSM'
            }];
        }
        if (['customeradmin', 'superadmin'].includes(role)) {
            roles.push({
                id: 'projectadmin',
                name: 'Project Admin'
            });
            roles.push({ id: 'gcologist', name: 'GCologist' });
        }
        roles.push({
            id: 'user',
            name: 'User'
        });
        setRoleList(roles);
    }, [userDetails]);

    const handleInputChange = (evt) => {
        const { name, value } = evt.target;
        setFormState((prev) => (
            name === 'email'
                ? { ...prev, email: value, userName: value }
                : { ...prev, [name]: value }
        ));
    }

    const handleFieldsChange = (patch) => {
        setFormState((prev) => ({ ...prev, ...patch }));
    }

    const formValidationCheck = () => {
        if (impersonation.active) {
            notify('error', IMPERSONATION_BLOCKED_MESSAGE);
            return null;
        }
        if (!formState.firstName || !formState.lastName || !formState.userName || !formState.email || !formState.location || customers.length === 0) {
            notify("error", REQUIRED_ERROR);
            return null;
        }
        if (!checkValidEmail(formState.email)) {
            notify("error", "Please enter valid Email ID");
            return null;
        }
        const programmeType = formState.project?.programme_type || '';
        const programmeProfileFields = profileFieldsForProgramme(programmeOptions, programmeType);
        const profileError = validateProfileFields(formState, { programmeType, profileFields: programmeProfileFields });
        if (profileError) {
            notify("error", profileError);
            return null;
        }
        return handleFormSubmit();
    }

    const handleFormSubmit = async () => {
        if (impersonation.active) {
            notify('error', IMPERSONATION_BLOCKED_MESSAGE);
            return null;
        }
        setLoading(true);
        let customersIds = customers.map(cs => cs.uuid);
        const programmeType = formState.project?.programme_type || '';
        const programmeProfileFields = profileFieldsForProgramme(programmeOptions, programmeType);
        const payload = applyProfileToPayload({
            ...formState,
            userName: formState.email,
            is_active: true,
            customers: customersIds,
            project_id: formState.project ? formState.project.id : ''
        }, formState, { programmeType, profileFields: programmeProfileFields });
        const storedAuth = readStoredUserAuth();
        const role = loggedInRole(userDetails, storedAuth);
        if (role === USER_ROLES.PROJECT_ADMIN || role === USER_ROLES.CSM_ADMIN) {
            const projectId = associatedProjectId({ userDetails, storedAuth });
            if (projectId) {
                const selectedProj = projectList.find(itm => itm.id === projectId);
                if (selectedProj) payload.project = selectedProj;
                payload.project_id = projectId;
            }
        }
        const result = await userServices.addUser(payload);
        if (result && result.status) {
            notify("success", result.message)
            setLoading(false);
            history.push(Paths.Users);
        } else {
            setLoading(false);
            notify("error", formatApiErrorMessage(result));
        }
    }

    const handleChangeCustomer = (value, type) => {
        if (type === 'project') {
            setFormState(clearInactiveProgrammeFields({ ...formState, project: value }, value?.programme_type || ''));
        } else {
            setCustomers(value);
            if (value && value[0]) {
                return getProjectList(value[0]?.uuid);
            }
            setProjectList([]);
        }
    }

    const handleBulkUploadDialog = () => {
        setOpenBulkUploadDialog(true)
    }

    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    <div className={classes.pageHeader}>
                        <Typography
                            variant="subtitle2"
                            className={classes.heading}
                            color="textPrimary">
                            Create User
                        </Typography>
                        <Button color="primary" variant="outlined" className={classes.btn} onClick={handleBulkUploadDialog} disabled={impersonation.active}>
                            Bulk upload
                        </Button>
                    </div>
                    <CreateUserForm
                        projectList={projectList}
                        formState={formState}
                        handleChangeCustomer={handleChangeCustomer}
                        handleInputChange={handleInputChange}
                        companyList={companyList}
                        roleList={roleList}
                        userDetails={userDetails}
                        customers={customers}
                        adminFieldsLocked={impersonation.active}
                        showProfileFields
                        handleFieldsChange={handleFieldsChange}
                        programmeType={formState.project?.programme_type || ''}
                        programmeTypeLabel={formState.project?.programme_type_label || ''}
                        programmeProfileFields={profileFieldsForProgramme(programmeOptions, formState.project?.programme_type || '')}
                    />
                    <div className={classes.placeBtn}>
                        <Button onClick={() => history.push(Paths.Users)} variant="outlined" color="default" className={classes.cancelBtn}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            className={classes.btn}
                            variant="contained"
                            disabled={
                                impersonation.active ||
                                !formState.firstName ||
                                !formState.lastName ||
                                !formState.userName ||
                                !formState.email ||
                                !formState.role ||
                                !formState.location
                            }
                            onClick={formValidationCheck}>
                            {loading ? <CircularProgress size={15} color="secondary" /> : 'Save'}
                        </Button>
                    </div>
                    <UserBulkUpload
                        open={openBulkUploadDialog}
                        handleClose={() => setOpenBulkUploadDialog(false)}
                        projectId={formState.project?.id || ''}
                    />
                </div>
            </main>
        </Layout>
    );
}

export default AddUsers;
